/**
 * ASO-AN-001 — RevenueCat identity lifecycle.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE ONE THING THAT MUST NOT CHANGE: `app_user_id` IS THE ORGANIZATION
 *
 * An Atlas entitlement is owned by the ORGANIZATION, not by the person who tapped Buy. Every user
 * of an org shares the plan, and the plan outlives any individual account. Production is built on
 * that and would break in three separate places if the App User ID became a person id:
 *
 *   1. `apps/atlas-backend/src/modules/webhooks/revenuecat-webhook.service.ts` resolves the
 *      organization to write subscription state for FROM `event.app_user_id`. A person id there
 *      resolves to no organization, and the webhook throws — which, because it throws to force a
 *      provider retry, means the subscription state is never recorded at all.
 *   2. `screens/Billing/BillingScreen.js` reads `/v1/subscribers/{organizationId}` directly over
 *      REST for the web plan view.
 *   3. Every existing paying customer's entitlement is filed under their organization id. Re-keying
 *      the identity orphans all of them — a live billing outage, not a metrics regression.
 *
 * So this module never sends a Firebase UID to RevenueCat, and deliberately does NOT set the
 * `$posthogUserId` reserved attribute. That attribute makes RevenueCat's PostHog integration
 * attribute an org's subscription lifecycle to one PERSON, which is a false claim about who owns
 * the entitlement — the joins it would enable would be wrong, quietly, forever. Subscription
 * lifecycle is joined to product behaviour at the PostHog `organization` group instead; the
 * backend webhook emits that group-scoped event, and the queries are in
 * docs/technical/analytics-identity.md.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT WAS ACTUALLY BROKEN HERE
 *
 * Identity was established exclusively by `Purchases.configure({ appUserID })`, inside the mount
 * effect of BillingScreen and PaywallScreen. Two consequences:
 *
 *   * `configure` is not how the RevenueCat SDK switches the current user — `logIn` is. When a
 *     device changed organization (an account switch to a user in a different org), the second
 *     `configure` is not a supported user switch, so the SDK could keep serving the previous org's
 *     customer info: one org reading another org's entitlement.
 *   * There was no `logOut` anywhere, so after a logout the device kept the departed org's
 *     RevenueCat identity for the life of the process.
 *
 * `resolveRevenueCatTransition` makes that decision a pure function so both cases are asserted by
 * tests rather than inferred from effect ordering.
 *
 * Like the PostHog module, nothing here is load-bearing: a RevenueCat failure must never stop a
 * screen from rendering or a till from opening.
 */

/**
 * Decide how to bring the RevenueCat SDK onto `organizationId`.
 *
 * @param {{configured?: boolean, currentAppUserId?: string | null, organizationId?: string | null}} state
 * @returns {'skip' | 'configure' | 'login' | 'noop'}
 */
export function resolveRevenueCatTransition({ configured, currentAppUserId, organizationId }) {
  // No organization to bill. Configuring against an anonymous id would create a RevenueCat
  // subscriber nobody owns, so do nothing at all.
  if (!organizationId) return 'skip'
  // First use in this process: the SDK has no identity yet and `configure` is the only way to give
  // it one (`logIn` before `configure` rejects).
  if (!configured) return 'configure'
  // Already on this organization. Re-configuring is not free — it resets SDK state — and re-logging
  // in issues a needless network call.
  if (currentAppUserId === organizationId) return 'noop'
  // Configured, but on a DIFFERENT organization. This is the switch that `configure` does not
  // perform. `logIn` is the documented transition and is what actually re-points the SDK.
  return 'login'
}

/**
 * Bring the RevenueCat SDK onto the given organization, configuring it first if needed.
 *
 * @param {{
 *   purchases: object | null | undefined,
 *   apiKey?: string,
 *   organizationId?: string | null,
 *   attributes?: Record<string, string | null>,
 * }} params
 * @returns {Promise<'skip' | 'configure' | 'login' | 'noop' | 'error'>} what was applied
 */
export async function applyRevenueCatIdentity({ purchases, apiKey, organizationId, attributes }) {
  if (!purchases) return 'skip'
  try {
    const configured = (await purchases.isConfigured?.()) === true
    // `getAppUserID` rejects when the SDK is not configured yet, so it is only asked once we know
    // it is — otherwise the rejection would be read as "identity unknown" and force a needless
    // re-configure.
    const currentAppUserId = configured ? await purchases.getAppUserID?.() : null

    const action = resolveRevenueCatTransition({ configured, currentAppUserId, organizationId })
    if (action === 'skip') return action
    if (action === 'configure') purchases.configure?.({ apiKey, appUserID: organizationId })
    if (action === 'login') await purchases.logIn?.(organizationId)

    // Attributes are org-scoped descriptive data for the RevenueCat dashboard. They are re-set on
    // every alignment because a `logIn` moves to a different subscriber, whose attributes are its
    // own. Nothing here is a person identity claim.
    if (attributes && Object.keys(attributes).length > 0) {
      await purchases.setAttributes?.(attributes)
    }
    return action
  } catch (error) {
    console.warn('[revenuecat] identity alignment failed; billing UI still renders', error)
    return 'error'
  }
}

/**
 * Drop the RevenueCat identity on sign-out, so the next account on this device does not inherit the
 * previous organization's entitlement view.
 *
 * `logOut` is a no-op-with-error when the current subscriber is already anonymous, so that case is
 * checked rather than caught: an expected condition should not surface as an error line.
 *
 * @param {{purchases: object | null | undefined}} params
 * @returns {Promise<boolean>} true when an identity was actually cleared
 */
export async function clearRevenueCatIdentity({ purchases }) {
  if (!purchases) return false
  try {
    if ((await purchases.isConfigured?.()) !== true) return false
    if ((await purchases.isAnonymous?.()) === true) return false
    await purchases.logOut?.()
    return true
  } catch (error) {
    console.warn('[revenuecat] logout failed; analytics identity is still cleared', error)
    return false
  }
}
